import {
  IRecommendTemplate,
  getRecommendTemplate,
} from '@/services/textDetail';
import { Button } from '@ai-indeed/idp-ui';
import { Form, Modal, message } from 'antd';
import type { ModalProps } from 'antd/lib/modal';
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { IconFont } from '../../../IconFont';
import { DEFAULT_ACCEPT, IMAGE_TYPES, MAX_TASK_COUNT } from '../../const';
import type { IConfirmParams, IUploadWithCustomAttrs } from '../../type';
import { isImage } from '../../utils';
import { FileUploader } from '../FileUploader';
import { ImageUploader } from '../ImageUploader';
import { TemplateArea } from '../TemplateArea';
import { UploadEntrance } from '../UploadEntrance';
import './index.less';

/**
 * 430 OCR部分模板支持上传文件 支持多张图片拼接成一个图片===》根据是否能够上传文件判断生成的任务
 *
 * */

export interface IProps extends ModalProps {
  showTemplateArea?: boolean;
  initialFileList?: IUploadWithCustomAttrs[];
  onConfirm?: (value: IConfirmParams) => void;
  uploadData: { [key: string]: string | boolean | number };
  module?: string /** 暂不支持 */;
  accept?: string /** 暂不支持 */;
  fileTypes?: string[];
  maxTaskCount?: number;
}

export const UploadModal: FC<Omit<IProps, 'onOk'>> = (props) => {
  const {
    open,
    width = 622,
    title = '文件上传',
    confirmLoading,
    uploadData,
    module = '',
    accept = DEFAULT_ACCEPT,
    showTemplateArea,
    maxTaskCount = MAX_TASK_COUNT,
    initialFileList = [],
    footer,
    ...restProps
  } = props;
  const [maxHeight, setMaxHeight] = useState(620);
  const [form] = Form.useForm();
  const [tasksType, setTasksType] = useState<boolean[]>([]);
  const [restCount, setRestCount] = useState(maxTaskCount);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [templateList, setTemplateList] = useState<IRecommendTemplate[]>([]);
  const [fileTypes, setFileTypes] = useState<string>('');
  const [imageTypes, setImageTypes] = useState<string>('');

  useEffect(() => {
    const list = accept?.split(',') || [];
    const list1 = [],
      list2 = [];
    for (const v of list) {
      if (IMAGE_TYPES.includes(v)) {
        list1.push(v);
      } else {
        list2.push(v);
      }
    }
    setImageTypes(list1.join(','));
    setFileTypes(list2.join(','));
  }, [accept]);

  const resizeUpdate = () => {
    const height = window.innerHeight;
    let maxHeight = 620;
    if (height >= 900) {
      maxHeight = 680;
    } else if (height >= 1080) {
      maxHeight = 760;
    }
    setMaxHeight(maxHeight);
  };

  useLayoutEffect(() => {
    window.addEventListener('resize', resizeUpdate);
    return () => {
      window.removeEventListener('resize', resizeUpdate);
    };
  }, []);

  const geneCommonTasks = (fileList: IUploadWithCustomAttrs[]) => {
    const tasks: IUploadWithCustomAttrs[][] = [];
    const types: boolean[] = [];
    const imageList = [];

    for (const file of fileList) {
      if (isImage(file.type || '')) {
        imageList.push(file);
      } else {
        tasks.push([file]);
        types.push(false);
      }
    }
    if (imageList.length > 0) {
      tasks.push(imageList);
      types.push(true);
    }
    return { tasks, types };
  };

  const geneOcrTasks = (fileList: IUploadWithCustomAttrs[]) => {
    const tasks: IUploadWithCustomAttrs[][] = [];
    const types: boolean[] = [];
    for (const file of fileList) {
      if (isImage(file.type || '')) {
        tasks.push([file]);
        types.push(true);
      } else {
        tasks.push([file]);
        types.push(false);
      }
    }
    return { tasks, types };
  };

  const geneTasks = (fileList: IUploadWithCustomAttrs[]) => {
    if (fileTypes.length === 0) {
      return geneOcrTasks(fileList);
    }
    return geneCommonTasks(fileList);
  };

  const addTasks = (fileList: IUploadWithCustomAttrs[]) => {
    const { tasks: newTasks, types } = geneTasks(fileList);
    if (newTasks.length > restCount) {
      message.warning('最多支持' + MAX_TASK_COUNT + '份文件');
    }
    const preTasks = form.getFieldValue('tasks') || [];
    const tasks = [...preTasks, ...newTasks].slice(0, MAX_TASK_COUNT);
    form.setFieldValue('tasks', [...tasks]);
    setTasksType((pre) => [...pre, ...types].slice(0, MAX_TASK_COUNT));
    setRestCount(tasks.length >= 10 ? 0 : MAX_TASK_COUNT - tasks.length);
  };

  const initForm = (fileList: IUploadWithCustomAttrs[]) => {
    const { tasks, types } = geneTasks(fileList);
    if (tasks.length > maxTaskCount) {
      message.warning(`最多支持${maxTaskCount}份文件`);
    }
    form.setFieldValue('tasks', tasks.slice(0, maxTaskCount));
    setTasksType((pre) => pre.concat(types.slice(0, maxTaskCount)));
    setRestCount(
      maxTaskCount - tasks.length < 0 ? 0 : maxTaskCount - tasks.length,
    );
  };

  const getTemplates = () => {
    getRecommendTemplate('').then((res) => {
      setTemplateList(res.data.records);
    });
  };

  useEffect(() => {
    if (!open) {
      form.setFieldsValue({ tasks: [] });
      setSelectedIds([]);
      setTasksType([]);
    } else {
      setRestCount(MAX_TASK_COUNT);
      initForm(initialFileList);
      if (showTemplateArea) {
        getTemplates();
      }
    }
  }, [open, initialFileList]);

  const fromatTasks = (
    taskList: IUploadWithCustomAttrs[][],
  ): IConfirmParams['tasks'] => {
    const tasks: IConfirmParams['tasks'] = [];
    for (const task of taskList) {
      const list = [];
      for (const file of task) {
        if (file.response && file.response.code === 0) {
          list.push(file.response.data.fileHash);
        }
      }
      if (list.length > 0) {
        tasks.push({
          task_name: task[0].name.slice(0, 80),
          file_hash_list: list,
        });
      }
    }
    return tasks;
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { onCancel } = props;
    setSelectedIds([]);
    onCancel?.(e);
  };

  const handleConfirm = () => {
    const { onConfirm } = props;
    const taskList: IUploadWithCustomAttrs[][] = form.getFieldValue('tasks');
    const tasks = fromatTasks(taskList);
    if (tasks.length === 0) {
      message.error('请上传文件');
      return;
    }
    onConfirm?.({
      tasks: [...tasks],
      templateCodes: [...selectedIds],
      originData: taskList,
    });
  };

  const onRemove = (index: number) => {
    setTasksType((pre) => {
      const list = [...pre];
      list.splice(index, 1);
      return list;
    });
    setRestCount((pre) => pre + 1);
  };

  return (
    <Modal
      open={open}
      width={width}
      {...restProps}
      title={title}
      onCancel={handleCancel}
      styles={{ body: { padding: 0 } }}
      className="um-wrapper"
      footer={
        footer || (
          <>
            <Button type="outline1" size="large" onClick={handleCancel}>
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleConfirm}
              loading={confirmLoading}
            >
              确定
            </Button>
          </>
        )
      }
    >
      <div className="um-main" style={{ maxHeight }}>
        <UploadEntrance
          module={module}
          restCount={restCount}
          data={uploadData}
          accept={accept}
          finished={addTasks}
        >
          <button type="button" className="um-upload-btn">
            <IconFont
              type="icon-shangchuan"
              className="text-[20px] mr-2"
            ></IconFont>
            <span className="text-sm">上传文件</span>
          </button>
        </UploadEntrance>
        <Form form={form}>
          <Form.List name="tasks">
            {(fields, { remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} className="um-main__task relative group">
                    <IconFont
                      type="icon-qingchuwenjian"
                      className="absolute -top-1 -right-1 invisible group-hover:visible"
                      onClick={() => {
                        remove(index);
                        onRemove(index);
                      }}
                    ></IconFont>
                    <Form.Item {...field} noStyle>
                      {tasksType[index] ? (
                        <ImageUploader
                          data={uploadData}
                          module={module}
                          accept={imageTypes}
                          multiple={!!fileTypes}
                          // multiple={module !== 'OCR' && module !== 'API'}
                        />
                      ) : (
                        <FileUploader data={uploadData} accept={fileTypes} />
                      )}
                    </Form.Item>
                  </div>
                ))}
              </>
            )}
          </Form.List>
        </Form>
        {showTemplateArea && (
          <TemplateArea
            className="um-main__template"
            data={templateList}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
          />
        )}
      </div>
    </Modal>
  );
};
